/**
 * Updates settings section.
 *
 * On-demand "Check for updates" button. The actual install flow happens
 * via the top-bar `UpdateIndicator` — this section is for users who want
 * to verify they are on the latest version without waiting for the hourly
 * background check.
 */

import type { CSSProperties } from 'react'
import { Download } from 'lucide-react'

import { SettingsSection } from './SettingsSection'
import { Button } from '@/shared/components/ui/button'
import { useUpdateStore } from '@/shared/stores'
import { IS_TAURI } from '@/lib/platform'

/**
 * Restart the desktop app after an update installs (Task 4093).
 *
 * Lazily imported so `@tauri-apps/plugin-process` stays out of the browser
 * bundle. Unreachable there in any case: the only path to this button is
 * update status 'ready', which requires the Tauri-gated updater to have run.
 */
function relaunchApp(): void {
  // The IS_TAURI guard is what lets Rollup drop `plugin-process` from the web
  // bundle. An unguarded dynamic import is still emitted as a chunk, because
  // the bundler cannot prove the call is unreachable — being behind a build-time
  // constant is the whole mechanism (Task 4093).
  if (!IS_TAURI) return
  void import('@tauri-apps/plugin-process')
    .then(({ relaunch }) => relaunch())
    .catch(() => { /* handled by Tauri's own error UI */ })
}

export function UpdatesSection() {
  const status = useUpdateStore(s => s.status)
  const checkForUpdate = useUpdateStore(s => s.checkForUpdate)
  const downloadAndInstall = useUpdateStore(s => s.downloadAndInstall)

  const checking = status.kind === 'checking'
  const downloadingOrInstalling =
    status.kind === 'downloading' || status.kind === 'installing'

  return (
    <SettingsSection
      id="settings-updates"
      title="Updates"
      description="Stream checks for new versions automatically every hour. You can also check now."
    >
      <div style={styles.row}>
        <div style={styles.iconAndText}>
          <div style={styles.iconWrapper}>
            <Download style={styles.icon} />
          </div>
          <div style={styles.textWrapper}>
            <span style={styles.label}>Check for updates</span>
            <StatusLine status={status} />
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void checkForUpdate()}
          disabled={checking || downloadingOrInstalling}
        >
          {checking ? 'Checking…' : 'Check now'}
        </Button>
      </div>

      {/* When an update is available, surface a one-click action here too,
          so users on the Settings page don't have to hunt for the top-bar
          indicator. */}
      {status.kind === 'update_available' && (
        <div style={styles.row}>
          <div style={styles.iconAndText}>
            <div style={styles.textWrapper}>
              <span style={styles.label}>
                Version {status.latestVersion} available
              </span>
              <span style={styles.hint}>
                You are on {status.currentVersion}
                {status.fileSizeBytes !== null
                  ? ` · ${(status.fileSizeBytes / (1024 * 1024)).toFixed(1)} MB`
                  : ''}
              </span>
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void downloadAndInstall()}
          >
            Download and install
          </Button>
        </div>
      )}

      {status.kind === 'ready' && (
        <div style={styles.row}>
          <div style={styles.iconAndText}>
            <div style={styles.textWrapper}>
              <span style={styles.label}>
                Update installed
              </span>
              <span style={styles.hint}>
                Restart Stream to run version {status.latestVersion}.
              </span>
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              relaunchApp()
            }}
          >
            Restart now
          </Button>
        </div>
      )}
    </SettingsSection>
  )
}

// ── Status line ─────────────────────────────────────────────────────────────

interface StatusLineProps {
  status: ReturnType<typeof useUpdateStore.getState>['status']
}

function StatusLine({ status }: StatusLineProps) {
  switch (status.kind) {
    case 'idle':
      return <span style={styles.hint}>Not checked this session.</span>
    case 'checking':
      return <span style={styles.hint}>Checking for updates…</span>
    case 'up_to_date':
      return (
        <span style={styles.hint}>
          You are on the latest version ({status.version}).
        </span>
      )
    case 'needs_reauth':
      return (
        <span style={styles.hintWarning}>
          Sign out and sign back in to enable update checking.
        </span>
      )
    case 'update_available':
      return (
        <span style={styles.hintAccent}>
          Version {status.latestVersion} available.
        </span>
      )
    case 'downloading': {
      const pct =
        status.totalBytes > 0
          ? Math.round((status.bytesDownloaded / status.totalBytes) * 100)
          : 0
      return <span style={styles.hint}>Downloading update… {pct}%</span>
    }
    case 'installing':
      return <span style={styles.hint}>Installing update…</span>
    case 'ready':
      return (
        <span style={styles.hintSuccess}>
          Update installed. Restart Stream to apply.
        </span>
      )
    case 'error':
      return <span style={styles.hintError}>Last check failed: {status.message}</span>
  }
}

const styles: Record<string, CSSProperties> = {
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  },
  iconAndText: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
  },
  iconWrapper: {
    display: 'flex',
    width: '40px',
    height: '40px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-tertiary)',
    flexShrink: 0,
  },
  icon: {
    width: '20px',
    height: '20px',
    color: 'var(--text-muted)',
  },
  textWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  hint: {
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  hintAccent: {
    fontSize: '12px',
    color: 'var(--accent-primary, #60a5fa)',
  },
  hintSuccess: {
    fontSize: '12px',
    color: 'var(--success, #22c55e)',
  },
  hintWarning: {
    fontSize: '12px',
    color: 'var(--warning, #f59e0b)',
  },
  hintError: {
    fontSize: '12px',
    color: 'var(--error, #ef4444)',
    wordBreak: 'break-word',
  },
}
